import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  Folder, 
  Music2, 
  Search, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  FolderOpen,
  HardDrive,
  RefreshCw,
  Upload,
  X
} from 'lucide-react';
import { Input } from './ui/input';
import {
  isFileSystemAccessSupported,
  requestFolderAccess,
  getStoredFolderHandle,
  verifyPermission,
  scanForAudioFiles,
  getFileFromHandle
} from '../utils/localFileManager';
import { analyzeAudio } from '../utils/audioAnalyzer';
import api from '../utils/api';

/**
 * LocalFileBrowser - Professional file browser for accessing local audio files
 * Updated to light/violet theme
 */
const LocalFileBrowser = ({ 
  isOpen, 
  onClose, 
  playlistId,
  eventId,
  onUploadComplete 
}) => {
  const [isSupported, setIsSupported] = useState(false);
  const [folderHandle, setFolderHandle] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [scanning, setScanning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [currentFolderName, setCurrentFolderName] = useState('');

  // Check API support on mount
  useEffect(() => {
    setIsSupported(isFileSystemAccessSupported());
  }, []);

  // Try to restore previous folder access
  useEffect(() => {
    const restoreFolder = async () => {
      const storedHandle = await getStoredFolderHandle();
      if (storedHandle) {
        const hasPermission = await verifyPermission(storedHandle);
        if (hasPermission) {
          setFolderHandle(storedHandle);
          setCurrentFolderName(storedHandle.name);
          scanFolder(storedHandle);
        }
      }
    };
    if (isOpen && isSupported) {
      restoreFolder();
    }
  }, [isOpen, isSupported]);

  // Scan folder for audio files
  const scanFolder = async (handle) => {
    setScanning(true);
    setAudioFiles([]);
    try {
      const files = await scanForAudioFiles(handle, '', 4);
      setAudioFiles(files);
      toast.success(`${files.length} fichier(s) audio trouvé(s)`);
    } catch (error) {
      console.error('Error scanning folder:', error);
      toast.error('Erreur lors du scan du dossier');
    } finally {
      setScanning(false);
    }
  };

  // Handle folder selection
  const handleSelectFolder = async () => {
    try {
      const handle = await requestFolderAccess();
      if (handle) {
        setFolderHandle(handle);
        setCurrentFolderName(handle.name);
        setSelectedFiles([]);
        await scanFolder(handle);
      }
    } catch (error) {
      console.error('Error selecting folder:', error);
      toast.error('Erreur lors de la sélection du dossier');
    }
  };

  // Toggle file selection
  const toggleFileSelection = (file) => {
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.path === file.path);
      if (isSelected) {
        return prev.filter(f => f.path !== file.path);
      } else {
        return [...prev, file];
      }
    });
  };

  // Select all filtered files
  const selectAll = () => {
    const filtered = filteredFiles;
    const allSelected = filtered.every(f => 
      selectedFiles.some(sf => sf.path === f.path)
    );
    if (allSelected) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filtered);
    }
  };

  // Filter files by search query
  const filteredFiles = audioFiles.filter(file => 
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Upload selected files with client-side analysis
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.warning('Sélectionnez au moins un fichier');
      return;
    }

    setUploading(true);
    const progress = selectedFiles.map(f => ({
      name: f.name,
      status: 'pending',
      progress: 0,
      bpm: null,
      duration: null,
      error: null
    }));
    setUploadProgress(progress);

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const fileInfo = selectedFiles[i];
      
      try {
        const file = await getFileFromHandle(fileInfo.handle);
        const title = file.name.replace(/\.[^/.]+$/, '');

        // Step 1: Client-side analysis
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'analyzing' } : p
        ));

        let analysisResult = { bpm: null, duration: null, waveform: null };
        try {
          const objectUrl = URL.createObjectURL(file);
          analysisResult = await analyzeAudio(objectUrl, 200);
          URL.revokeObjectURL(objectUrl);
          
          setUploadProgress(prev => prev.map((p, idx) => 
            idx === i ? { 
              ...p, 
              bpm: analysisResult.bpm,
              duration: analysisResult.duration,
              progress: 30
            } : p
          ));
        } catch (analysisError) {
          console.warn('Client-side analysis failed:', analysisError);
        }

        // Step 2: Upload file
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'uploading', progress: 40 } : p
        ));

        const formData = new FormData();
        formData.append('file', file);
        formData.append('playlist_id', playlistId);
        formData.append('title', title);
        formData.append('artist', 'Unknown');
        formData.append('order_index', i.toString());
        
        if (analysisResult.bpm) {
          formData.append('bpm', analysisResult.bpm.toString());
        }
        if (analysisResult.duration) {
          formData.append('duration_seconds', analysisResult.duration.toString());
        }
        if (analysisResult.waveform) {
          formData.append('waveform', JSON.stringify(analysisResult.waveform));
        }
        formData.append('skip_server_analysis', 'true');

        await api.post('/tracks/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
          onUploadProgress: (progressEvent) => {
            const uploadPercent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            const totalProgress = 40 + (uploadPercent * 0.6);
            setUploadProgress(prev => prev.map((p, idx) => 
              idx === i ? { ...p, progress: Math.round(totalProgress) } : p
            ));
          }
        });

        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'completed', progress: 100 } : p
        ));
        successCount++;

      } catch (error) {
        console.error(`Error uploading ${fileInfo.name}:`, error);
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { 
            ...p, 
            status: 'error', 
            error: error.response?.data?.detail || 'Erreur d\'upload'
          } : p
        ));
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} fichier(s) uploadé(s) avec succès !`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} fichier(s) en erreur`);
    }

    setTimeout(() => {
      setUploading(false);
      setSelectedFiles([]);
      setUploadProgress([]);
      if (successCount > 0 && onUploadComplete) {
        onUploadComplete();
      }
      onClose();
    }, 2000);
  };

  // Render upload progress
  const renderUploadProgress = () => {
    const completedCount = uploadProgress.filter(p => p.status === 'completed').length;
    const errorCount = uploadProgress.filter(p => p.status === 'error').length;
    const totalCount = uploadProgress.length;
    
    return (
      <div className="space-y-3 mt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">Progression de l&apos;upload</h4>
          <span className="text-xs text-gray-500">
            {completedCount}/{totalCount} terminé(s)
            {errorCount > 0 && <span className="text-red-500 ml-2">• {errorCount} erreur(s)</span>}
          </span>
        </div>
        
        <div className="max-h-[300px] overflow-y-auto pr-2 space-y-2">
          {uploadProgress.map((item, idx) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-900 truncate max-w-[250px]" title={item.name}>
                  {item.name}
                </span>
                <span className="text-xs flex items-center gap-2">
                  {item.status === 'pending' && <span className="text-gray-400">En attente</span>}
                  {item.status === 'analyzing' && (
                    <span className="text-blue-500 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Analyse BPM...
                    </span>
                  )}
                  {item.status === 'uploading' && (
                    <span className="text-violet-600 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Upload...
                    </span>
                  )}
                  {item.status === 'completed' && <span className="text-emerald-500">✓ Terminé</span>}
                  {item.status === 'error' && (
                    <span className="text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Erreur
                    </span>
                  )}
                </span>
              </div>
              <Progress value={item.progress} className="h-1" />
              {item.bpm && item.duration && (
                <p className="text-xs text-gray-500 mt-1">
                  BPM: {Math.round(item.bpm)} | Durée: {Math.floor(item.duration / 60)}:{String(Math.floor(item.duration % 60)).padStart(2, '0')}
                </p>
              )}
              {item.error && (
                <p className="text-xs text-red-500 mt-1 truncate" title={item.error}>
                  {item.error}
                </p>
              )}
            </div>
          ))}
        </div>
        
        {uploadProgress.every(p => p.status === 'completed' || p.status === 'error') && (
          <div className="flex justify-end pt-2">
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
            >
              Fermer
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Not supported fallback
  if (!isSupported) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Navigateur non supporté
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              Votre navigateur ne supporte pas l&apos;API File System Access. 
              Cette fonctionnalité nécessite Chrome, Edge ou Opera.
            </p>
            <p className="text-sm text-gray-500">
              Utilisez l&apos;upload classique via le bouton &quot;Ajouter Musiques&quot;.
            </p>
            <Button onClick={onClose} variant="outline" className="w-full">
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={uploading ? undefined : onClose}>
      <DialogContent 
        className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        aria-describedby="local-browser-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-violet-600" />
            Bibliothèque Locale
          </DialogTitle>
          <p id="local-browser-description" className="sr-only">
            Sélectionnez un dossier local pour importer des fichiers audio
          </p>
        </DialogHeader>

        {uploading ? (
          renderUploadProgress()
        ) : (
          <>
            {/* Folder Selection */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Button
                onClick={handleSelectFolder}
                disabled={scanning}
                variant="outline"
                className="border-violet-300 text-violet-600 hover:bg-violet-50"
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                {folderHandle ? 'Changer de dossier' : 'Sélectionner un dossier'}
              </Button>
              
              {currentFolderName && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Folder className="w-4 h-4" />
                  <span className="truncate max-w-[200px]">{currentFolderName}</span>
                  {scanning && <Loader2 className="w-4 h-4 animate-spin text-violet-600" />}
                </div>
              )}

              {folderHandle && !scanning && (
                <Button
                  onClick={() => scanFolder(folderHandle)}
                  size="icon"
                  variant="ghost"
                  className="text-gray-500 hover:text-violet-600"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Search */}
            {audioFiles.length > 0 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher..."
                  className="pl-10 bg-gray-50 border-gray-200 text-gray-900"
                />
              </div>
            )}

            {/* File List */}
            <div className="flex-1 overflow-y-auto min-h-[200px] max-h-[350px] pr-2">
              {scanning ? (
                <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin mb-2 text-violet-600" />
                  <p>Scan du dossier en cours...</p>
                </div>
              ) : audioFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                  <Music2 className="w-8 h-8 mb-2" />
                  <p>Aucun fichier audio trouvé</p>
                  <p className="text-xs">Sélectionnez un dossier contenant des fichiers MP3, WAV, FLAC...</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Select All */}
                  <button
                    onClick={selectAll}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-violet-50 text-sm text-violet-600 sticky top-0 bg-white z-10 border-b border-gray-100"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {selectedFiles.length === filteredFiles.length && filteredFiles.length > 0
                      ? 'Tout désélectionner'
                      : `Tout sélectionner (${filteredFiles.length})`
                    }
                  </button>

                  {/* Files */}
                  {filteredFiles.map((file, idx) => {
                    const isSelected = selectedFiles.some(f => f.path === file.path);
                    return (
                      <button
                        key={file.path}
                        onClick={() => toggleFileSelection(file)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                          isSelected 
                            ? 'bg-violet-100 border border-violet-300' 
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'border-violet-600 bg-violet-600' 
                            : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </div>
                        <Music2 className="w-4 h-4 text-violet-500" />
                        <div className="flex-1 text-left">
                          <p className="text-sm text-gray-900 truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">{file.path}</p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {formatSize(file.size)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                {selectedFiles.length} fichier(s) sélectionné(s)
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="border-gray-300 text-gray-700"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Ajouter {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LocalFileBrowser;
